import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Stack,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  DataGrid,
  GridToolbar,
  GridActionsCellItem,
} from '@mui/x-data-grid';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  MoreVert as MoreIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  AttachMoney as MoneyIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Visibility as ViewIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Add as AddIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';

const statusColors = {
  Active: 'success',
  Pending: 'warning',
  Sold: 'default',
  OffMarket: 'error',
  UnderContract: 'info',
  ComingSoon: 'secondary',
};

const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Sold', label: 'Sold' },
  { value: 'OffMarket', label: 'Off Market' },
  { value: 'UnderContract', label: 'Under Contract' },
  { value: 'ComingSoon', label: 'Coming Soon' },
];

const typeOptions = [
  { value: 'Condo', label: 'Condo' },
  { value: 'House', label: 'House' },
  { value: 'Townhouse', label: 'Townhouse' },
  { value: 'Apartment', label: 'Apartment' },
  { value: 'Land', label: 'Land' },
  { value: 'Commercial', label: 'Commercial' },
  { value: 'MultiFamily', label: 'Multi-Family' },
];

function PropertyList({
  properties = [],
  loading = false,
  onPropertySelect,
  onPropertyEdit,
  onPropertyDelete,
  onBulkAction,
  selectedProperties = [],
  onSelectionChange,
  onAddProperty,
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPropertyForMenu, setSelectedPropertyForMenu] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Filter properties based on search and filters
  const filteredProperties = useMemo(() => {
    // Transform properties to include 'id' field for DataGrid
    const transformedProperties = properties.map((property) => ({
      ...property,
      id: property._id || property.id, // Use _id as id for DataGrid
    }));

    console.log('Transformed properties:', transformedProperties); // Debug log

    return transformedProperties.filter((property) => {
      const matchesSearch = searchTerm === ''
        || Object.values(property).some((val) => typeof val === 'string' && val.toLowerCase().includes(searchTerm.toLowerCase()))
        || (property.tags || []).some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesStatus = statusFilter === 'all' || property.status === statusFilter;
      const matchesType = typeFilter === 'all' || property.type === typeFilter;
      return matchesSearch && matchesStatus && matchesType;
    });
  }, [properties, searchTerm, statusFilter, typeFilter]);

  // DataGrid columns configuration
  const columns = [
    {
      field: 'address',
      headerName: 'Address',
      flex: 1.5,
      minWidth: 250,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HomeIcon color="primary" fontSize="small" />
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {params.value}
            </Typography>
            {params.row.mls && (
              <Typography variant="caption" color="text.secondary">
                MLS:
                {' '}
                {params.row.mls}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      flex: 0.6,
      minWidth: 100,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          color="primary"
        />
      ),
    },
    {
      field: 'price',
      headerName: 'Price',
      flex: 0.8,
      minWidth: 120,
      type: 'number',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon fontSize="small" color="success" />
          <Typography variant="body2" fontWeight={600}>
            $
            {params.value?.toLocaleString()}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.6,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={statusColors[params.value] || 'default'}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: 'agent',
      headerName: 'Agent',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon fontSize="small" color="action" />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'bedrooms',
      headerName: 'Beds',
      flex: 0.4,
      minWidth: 60,
      type: 'number',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'bathrooms',
      headerName: 'Baths',
      flex: 0.4,
      minWidth: 60,
      type: 'number',
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={600}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'sqft',
      headerName: 'Sq Ft',
      flex: 0.5,
      minWidth: 80,
      type: 'number',
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value?.toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'watched',
      headerName: 'Watched',
      flex: 0.4,
      minWidth: 80,
      type: 'boolean',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {params.value ? (
            <FavoriteIcon fontSize="small" color="error" />
          ) : (
            <FavoriteBorderIcon fontSize="small" color="action" />
          )}
        </Box>
      ),
    },
    {
      field: 'features',
      headerName: 'Features',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {(params.value || []).slice(0, 2).map((feature, index) => (
            <Chip
              key={index}
              label={feature}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          ))}
          {(params.value || []).length > 2 && (
            <Chip
              label={`+${(params.value || []).length - 2}`}
              size="small"
              variant="outlined"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          )}
        </Stack>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Listed',
      flex: 0.6,
      minWidth: 100,
      type: 'date',
      valueGetter: (params) => new Date(params.value),
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      flex: 0.5,
      minWidth: 100,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<ViewIcon />}
          label="View"
          onClick={() => onPropertySelect(params.row)}
          color="primary"
        />,
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => onPropertyEdit(params.row)}
          color="primary"
        />,
        <GridActionsCellItem
          icon={<MoreIcon />}
          label="More"
          onClick={(event) => {
            setSelectedPropertyForMenu(params.row);
            setAnchorEl(event.currentTarget);
          }}
          color="primary"
        />,
      ],
    },
  ];

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedPropertyForMenu(null);
  };

  const handleBulkAction = (action) => {
    if (selectedProperties.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select properties first',
        severity: 'warning',
      });
      return;
    }

    onBulkAction(action, selectedProperties);
    setSnackbar({
      open: true,
      message: `${action} applied to ${selectedProperties.length} properties`,
      severity: 'success',
    });
  };

  const handleDeleteProperty = () => {
    if (selectedPropertyForMenu) {
      onPropertyDelete(selectedPropertyForMenu);
      setSnackbar({
        open: true,
        message: 'Property deleted successfully',
        severity: 'success',
      });
    }
    handleMenuClose();
  };

  const handleToggleWatched = () => {
    if (selectedPropertyForMenu) {
      // Implement toggle watched functionality
      setSnackbar({
        open: true,
        message: `Property ${selectedPropertyForMenu.watched ? 'removed from' : 'added to'} watchlist`,
        severity: 'success',
      });
    }
    handleMenuClose();
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Debug info */}
      <Box sx={{
        p: 1, bgcolor: 'grey.100', fontSize: '12px', mb: 1,
      }}
      >
        Debug: Properties count:
        {' '}
        {properties.length}
        , Filtered:
        {' '}
        {filteredProperties.length}
        , Loading:
        {' '}
        {loading.toString()}
      </Box>

      {/* Header with search and filters */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Properties (
            {filteredProperties.length}
            )
          </Typography>
          <Box sx={{ flex: 1 }} />
          {selectedProperties.length > 0 && (
            <Alert severity="info" sx={{ py: 0 }}>
              {selectedProperties.length}
              {' '}
              property(ies) selected
            </Alert>
          )}
          <Tooltip title="Add New Property">
            <IconButton
              color="primary"
              onClick={onAddProperty}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
              }}
            >
              <AddIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            placeholder="Search properties..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, maxWidth: 400 }}
            size="small"
          />

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All Status</MenuItem>
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              label="Type"
            >
              <MenuItem value="all">All Types</MenuItem>
              {typeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedProperties.length > 0 && (
            <Stack direction="row" spacing={1}>
              <Tooltip title="Archive Selected">
                <IconButton
                  size="small"
                  onClick={() => handleBulkAction('archive')}
                  sx={{
                    borderColor: 'warning.main',
                    color: 'warning.main',
                    '&:hover': { bgcolor: 'warning.light' },
                  }}
                >
                  <ArchiveIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export Selected">
                <IconButton
                  size="small"
                  onClick={() => handleBulkAction('export')}
                  sx={{
                    borderColor: 'info.main',
                    color: 'info.main',
                    '&:hover': { bgcolor: 'info.light' },
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* DataGrid */}
      <Paper elevation={1} sx={{ flex: 1 }}>
        {filteredProperties.length === 0 && !loading ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            p: 4,
          }}
          >
            <HomeIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Properties Found
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {properties.length === 0
                ? 'No properties have been added yet. Click the + button to add your first property.'
                : 'No properties match your current search criteria. Try adjusting your filters.'}
            </Typography>
          </Box>
        ) : (
          <DataGrid
            rows={filteredProperties}
            columns={columns}
            loading={loading}
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={selectedProperties}
            onRowSelectionModelChange={onSelectionChange}
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 25 },
              },
              sorting: {
                sortModel: [{ field: 'createdAt', sort: 'desc' }],
              },
            }}
            components={{ Toolbar: GridToolbar }}
            onRowClick={(params) => onPropertySelect && onPropertySelect(params.row)}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          />
        )}
      </Paper>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={() => onPropertySelect(selectedPropertyForMenu)}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => onPropertyEdit(selectedPropertyForMenu)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Property</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleToggleWatched}>
          <ListItemIcon>
            {selectedPropertyForMenu?.watched ? (
              <FavoriteBorderIcon fontSize="small" color="error" />
            ) : (
              <FavoriteIcon fontSize="small" color="error" />
            )}
          </ListItemIcon>
          <ListItemText>
            {selectedPropertyForMenu?.watched ? 'Remove from Watchlist' : 'Add to Watchlist'}
          </ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDeleteProperty}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete Property</ListItemText>
        </MenuItem>
      </Menu>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PropertyList;
